import { IOption, IRoute } from '@/hooks/useMenu'

/**
 * Item do menu que abre uma tela. Os grupos (Cadastro, Operacional…) não entram aqui: eles só
 * expandem, não navegam — e o mesmo vale para o item de terceiro nível que é só um agrupador
 * (aquele cujo `route` é vazio).
 */
export interface IMenuLeaf {
  route: string
  title: string
  groupTitle: string
  groupRoute: string
}

export interface IMatchRange {
  start: number
  end: number
}

/**
 * Texto preparado para busca — minúsculo e sem acento — junto com o mapa de cada caractere
 * resultante para a posição dele no texto original. É esse mapa que permite destacar o trecho
 * casado sobre o texto acentuado: "análise" e "analise" acabam do mesmo tamanho, mas nem sempre é
 * assim (o "ﬁ" vira dois caracteres), então os índices do texto normalizado não servem direto.
 */
function foldForSearch(text: string): { value: string; indexes: number[] } {
  const chars: string[] = []
  const indexes: number[] = []

  for (let index = 0; index < text.length; index++) {
    const folded = text[index]
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()

    for (let position = 0; position < folded.length; position++) {
      chars.push(folded[position])
      indexes.push(index)
    }
  }

  return { value: chars.join(''), indexes }
}

/**
 * Onde o termo aparece no texto, em posições do texto ORIGINAL — é o que o destaque recorta.
 */
export function findMatchRange(text: string, term: string): IMatchRange | null {
  if (!text || !term) {
    return null
  }

  const folded = foldForSearch(text)
  const foldedTerm = foldForSearch(term).value

  if (!foldedTerm) {
    return null
  }

  const position = folded.value.indexOf(foldedTerm)

  if (position === -1) {
    return null
  }

  return {
    start: folded.indexes[position],
    end: folded.indexes[position + foldedTerm.length - 1] + 1
  }
}

export function matchesSearch(text: string, term: string): boolean {
  return findMatchRange(text, term) !== null
}

/**
 * A rota do menu é a tela atual — ou a tela atual é um detalhe dela, já que `/operations/123`
 * continua sendo "Operações". A barra no prefixo evita que `/titles` case com
 * `/title-confirmation`.
 */
export function isRouteActive(menuRoute: string, currentPath: string): boolean {
  if (!menuRoute || !currentPath) {
    return false
  }

  const path = currentPath.split('?')[0].split('#')[0]

  return path === menuRoute || path.startsWith(`${menuRoute}/`)
}

/**
 * Todos os itens navegáveis do menu já filtrado por permissão, na ordem em que aparecem na tela.
 */
export function collectMenuLeaves(config: IRoute[]): IMenuLeaf[] {
  const leaves: IMenuLeaf[] = []

  config?.forEach(route => {
    if (!route.isDropdown) {
      if (route.route) {
        leaves.push({
          route: route.route,
          title: route.title,
          groupTitle: route.title,
          groupRoute: route.route
        })
      }
      return
    }

    route.options?.forEach((option: IOption) => {
      if (option.options?.length) {
        option.options.forEach(subOption => {
          leaves.push({
            route: subOption.route,
            title: subOption.title,
            groupTitle: route.title,
            groupRoute: route.route
          })
        })
        return
      }

      if (option.route) {
        leaves.push({
          route: option.route,
          title: option.title,
          groupTitle: route.title,
          groupRoute: route.route
        })
      }
    })
  })

  return leaves
}

/**
 * Entre as rotas do menu que casam com a tela atual vence a mais específica: em
 * `/reports/titles/open` casam tanto o relatório quanto rotas mais curtas acima dele.
 */
export function findActiveLeaf(
  leaves: IMenuLeaf[],
  currentPath: string
): IMenuLeaf | null {
  const matches = leaves
    .filter(leaf => isRouteActive(leaf.route, currentPath))
    .sort((a, b) => b.route.length - a.route.length)

  return matches[0] || null
}

/**
 * O menu podado pelo termo buscado. Um grupo que casa pelo próprio nome vem inteiro; nos demais
 * sobram só os itens que casaram — e o grupo de terceiro nível conta como casado para os filhos
 * dele, senão buscar "Gerenciais" não traria nada.
 */
export function filterMenu(config: IRoute[], term: string): IRoute[] {
  if (!term) {
    return config
  }

  const filtered: IRoute[] = []

  config?.forEach(route => {
    const groupMatches = matchesSearch(route.title, term)

    if (!route.isDropdown) {
      if (groupMatches) {
        filtered.push(route)
      }
      return
    }

    if (groupMatches) {
      filtered.push(route)
      return
    }

    const options: IOption[] = []

    route.options?.forEach(option => {
      if (option.options?.length) {
        const parentMatches = matchesSearch(option.title, term)

        const subOptions = option.options.filter(
          subOption => parentMatches || matchesSearch(subOption.title, term)
        )

        if (subOptions.length) {
          options.push({ ...option, options: subOptions })
        }
        return
      }

      if (matchesSearch(option.title, term)) {
        options.push(option)
      }
    })

    if (options.length) {
      filtered.push({ ...route, options })
    }
  })

  return filtered
}
